import React from "react"
import {
    Box,
    CheckoutDatesPreview,
    componentStyles,
} from "@mallardbay/lib-react-components"

import type { QuoteFieldsFragment } from "~graphql/generated/graphql"
import {
    CheckoutRootContainer,
    CheckoutLeftContainer,
    CheckoutRightContainer,
} from "~components/pages/listing/checkout/checkout-layout-components"
import CheckoutTitle from "~components/pages/listing/checkout/checkout-title"
import CheckoutFlowSummaryContent from "~components/pages/listing/checkout/checkout-flow/checkout-flow-summary-content"
import CheckoutButtonsMobile from "~components/pages/listing/checkout/checkout-buttons-mobile"
import COPY from "~config/copy-constants"
import CheckoutSummaryPackages from "~components/pages/listing/checkout/checkout-summary-packages"
import CheckoutSummaryDrawer from "~components/pages/listing/checkout/checkout-summary-drawer"
import { getListingTypeFromQuoteOrBookingInvite } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-helpers"
import { getItineraryCheckInItems } from "~utils/itinerary-helpers"
import { useTrackBookingInviteViewed } from "~utils/event-hooks"
import type { OnEditPackageClickFunc } from "~components/shared/quote/quote-package-buttons"
import { useCheckoutFlow } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-provider"

import ContinueButton from "./continue-button"

interface Props {
    readonly quote: QuoteFieldsFragment
}

export default function CheckoutSummary({ quote }: Props) {
    const styles = useStyles()

    const { shouldShowDeleteButtons, shouldShowEditButtons } =
        getShouldShowButtonFlags(quote)

    const listingType = getListingTypeFromQuoteOrBookingInvite(quote)
    const dates = quote.dates.map((date) => new Date(date))

    const { checkInItem, checkOutItem } = getItineraryCheckInItems(
        quote.itinerary
    )
    const { goToFirstStep, isWidget } = useCheckoutFlow()

    // we also track this event on /checkout route, but we need to track it here if the booking invite goes straight to checkout-summary
    // without going through the checkout flow.  Since the event is stored in memory once fired its safe to have in both places.
    useTrackBookingInviteViewed(quote.booking_invite?.id)

    const handleEditPackageClick: OnEditPackageClickFunc = ({
        quoteId,
        quotePackageId,
        pricingPackageId,
    }) =>
        goToFirstStep({
            quote_id: quoteId,
            quote_package_id: quotePackageId,
            package_id: pricingPackageId,
            checkout_state: null,
        })
    const onEditPackageClick = shouldShowEditButtons
        ? handleEditPackageClick
        : undefined

    return (
        <>
            <CheckoutRootContainer isWidget={isWidget}>
                <CheckoutLeftContainer isWidget={isWidget}>
                    <Box style={styles.titleContainer}>
                        <CheckoutTitle
                            title={COPY.PACKAGES_SUMMARY}
                            noMargin={isWidget}
                        />
                    </Box>
                    <CheckoutDatesPreview
                        dates={dates}
                        listingType={listingType}
                        checkInItineraryItem={checkInItem}
                        checkOutItineraryItem={checkOutItem}
                    />
                    <CheckoutSummaryPackages quote={quote} />
                    <CheckoutButtonsMobile quote={quote} />
                </CheckoutLeftContainer>
                <CheckoutRightContainer isWidget={isWidget}>
                    <CheckoutFlowSummaryContent
                        listing={quote.listing}
                        quote={quote}
                        shouldShowDeleteButtons={shouldShowDeleteButtons}
                        onEditPackageClick={onEditPackageClick}
                    />
                </CheckoutRightContainer>
            </CheckoutRootContainer>
            <CheckoutSummaryDrawer
                quote={quote}
                listing={quote.listing}
                shouldShowDeleteButtons={shouldShowDeleteButtons}
                onEditPackageClick={onEditPackageClick}
                renderNavButtons={() => <ContinueButton />}
            />
        </>
    )
}

function useStyles() {
    return componentStyles({
        titleContainer: {
            marginBottom: "20px",
        },
    })
}

// Hide package delete buttons if there's only one package.
// Hide all buttons if the quote was created from a booking invite.
function getShouldShowButtonFlags(quote: QuoteFieldsFragment) {
    const isFromBookingInvite = Boolean(quote.booking_invite)
    const shouldShowDeleteButtons =
        !isFromBookingInvite && quote.packages.length > 1
    const shouldShowEditButtons = !isFromBookingInvite

    return { shouldShowDeleteButtons, shouldShowEditButtons }
}
